(ns app.front.view.group.admin.verdict-template
  (:require [app.domain.form.model :as form-model]
            [app.domain.group.model :as group-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.verdict.section :as verdict-section]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.ui :as ui]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.select :as ui-select]
            [app.front.ui.template-editor :as template-editor]
            [app.front.ui.text-editor :as text-editor]
            [app.front.util :as futil :refer [for-frag tval]]
            [app.front.view.group.admin.files :as group-files]
            [app.front.view.group.admin.review-config :as review-config]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.templates.language-selector :as lang-sel]
            [app.front.view.group.admin.verdict-template.edit :as verdict-template-edit]
            [app.front.view.group.admin.verdict-template.events :as vte]
            [app.front.view.group.subs :as group-subs]
            [cljs.reader]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-sub ::selected-element
  (fn [db [_ id]]
    (let [section (keyword id)]
      (-> db section :selected-element))))


(rf/reg-sub ::form-schemas :-> ::form-schemas)


(rf/reg-sub ::form-names
  :<- [::form-schemas]
  (fn [form-schemas _]
    (-> form-schemas keys sort)))


(rf/reg-sub ::form-fields
  :<- [::form-schemas]
  :<- [:language]
  :<- [::ff/enabled? :feature-flag.id/permit-pdf-table-form-fields]
  (fn [[form-schemas lang table-fields?] [_ form]]
    (->> (get form-schemas form)
         ;; Whole-table fields are only selectable when the feature flag is on.
         (remove #(and (= (:tag %) :table)
                       (not table-fields?)))
         distinct
         (sort-by #(or (form-model/label-text (:label %) lang)
                       (str (:field %)))))))


(rf/reg-sub ::form-and-field-for-content
  ;; The section only persists the selected field id (:verdict.section/content); map it
  ;; back to its form and field map so the picker shows the saved value after a reload.
  :<- [::form-schemas]
  (fn [form-schemas [_ content]]
    (when content
      (some (fn [[form fields]]
              (when-let [field (p/find-first fields {:field content})]
                {:form form :field field}))
            form-schemas))))


(rf/reg-sub ::labels #(-> % ::labels))


(rf/reg-sub ::label-edit?
  :<- [::labels]
  (fn [labels [_ id]]
    (boolean (get-in labels [id :edit?]))))


(rf/reg-sub ::party-roles
  (fn [[_ group-db-id]]
    [(rf/subscribe [:language])
     (rf/subscribe [::group-subs/available-party-roles group-db-id])])
  (fn [[lang available-party-roles] _]
    (->> available-party-roles
         (map (fn [party-role]
                {:role-key (:party-role/key party-role)
                 :label    (role-model/party-role-name party-role lang)}))
         (sort-by :label))))


(rf/reg-sub ::descendant-party-roles
  (fn []
    [(rf/subscribe [:language])
     (rf/subscribe [::entities/entities {:entity/type :entity.type/party-role}])
     (rf/subscribe [::gas/descendant-authority-group-ids])])
  (fn [[lang party-roles descendant-group-ids] [_ permit-type]]
    (->> party-roles
         (group-subs/filter-party-roles descendant-group-ids permit-type)
         (map (fn [party-role]
                {:role-key (:party-role/key party-role)
                 :label    (role-model/party-role-name party-role lang)}))
         (sort-by :label))))


(rf/reg-event-db ::edit-label
  (fn [db [_ id]]
    (assoc-in db [::labels id :edit?] true)))


(rf/reg-event-db ::cancel-edit-label
  (fn [db [_ id]]
    (p/dissoc-in db [::labels id])))


(rf/reg-event-db ::add-selection
  (fn [db [_ id value]]
    (let [section (keyword id)]
      (assoc-in db [section :selected-element] value))))


(rf/reg-event-db ::remove-selection
  (fn [db [_ id]]
    (let [section (keyword id)]
      (dissoc db section))))


(rf/reg-event-fx ::fetch-form-schemas
  (fn [_ [_ authority-group-id]]
    {:comm/command {:command    :group/form-schemas
                    :method     :get
                    :data       {:authority-group-id    authority-group-id
                                 :include-table-fields? true}
                    :on-success [::fetch-form-fields-success]}}))


(rf/reg-event-db
  ::fetch-form-fields-success
  (fn [db [_ _ raw-form-fields]]
    (assoc db ::form-schemas raw-form-fields)))

(rf/reg-sub ::any-permit-sign-off?
  :<- [::gas/group-id]
  :<- [::entities/entity-db]
  (fn [[group-id entity-db] _]
    (->> entity-db
         vals
         (filter #(-> % :group.config/group (= group-id)))
         (some :group.config/permit-sign-off?))))

(defmulti
  ^{:arglists '([ctx section])}
  render
  "Accepts a context and verdict (template) section. Returns a DOM hierarchy in hiccup format."
  (fn [_ section] (:verdict.section/type section)))

(defn section-title [tr section]
  (if (string? (:verdict.section/content section))
    (:verdict.section/content section)
    (or (verdict-section/get-label tr section)
        (tr [(:verdict.section/type section)]))))

(defn edit-label [tr group-id section]
  [ui/text-input {:id            (str "label-edit-" (:db/id section))
                  :label-text    ""
                  :hide-label    true
                  :light         true
                  :default-value (verdict-section/get-label tr section)
                  :on-change     #(rf/dispatch-sync [::vte/set-label-translation
                                                     group-id
                                                     (:db/id section)
                                                     (tval %)])}])

(rf/reg-sub ::contains-table-of-contents
  (fn [[_ section-id]]
    (rf/subscribe [::verdict-template-edit/siblings section-id]))
  (fn [subsections _]
    (->> subsections
         (map :verdict.section/type)
         (some #(= % :verdict.section.type/table-of-contents))
         boolean)))

(defn section-draft-selector [{:keys [tr section group-id]}]
  (when (<sub [::any-permit-sign-off?])
    (let [val  (boolean (:verdict.section/include-in-signable-draft? section))
          toc? (<sub [::contains-table-of-contents (:db/id section)])]
      [:div
       (cc/checkbox {:id         (str "include-" (:db/id section) "-in-draft")
                     :disabled   toc?
                     :label-text (tr [:verdict.section/include-in-signable-draft?])
                     :on-change  #(>evt [::vte/update-section
                                         group-id
                                         (:db/id section)
                                         {:verdict.section/include-in-signable-draft? (not val)}])
                     :checked    val})
       (when toc?
         [ui/help (tr [:verdict/table-of-contents-not-allowed-in-draft])])])))

(defn section-title-bar [{:keys [tr section group-id read-only?]}]
  (let [section-id  (:db/id section)
        toplevel?   (= (:db/id (:verdict.section/template section)) (:db/id (:entity/parent section)))
        type        (:verdict.section/type section)
        label-edit? (<sub [::label-edit? section-id])
        copy-of     (:verdict.section/copy-of section)]
    [:div.w-full.flex.flex-between.justify-between
     {:id section-id}
     [:div.lg:w-2-3.w-full
      [:div.flex.items-end.justify-between
       (if label-edit?
         [edit-label tr group-id section]
         [(if toplevel?
            :h3 :h4)
          {:class "flex-1 pr-2 mb-4"}
          (section-title tr section)])
       (when (:verdict.section/label section)
         [:div.mb-4 (tr [(:verdict.section/type section)])])
       (when (and (verdict-section/editable-section-types type)
                  (<sub [::lang-sel/show-selector?]))
         [:div.ml-4
          [lang-sel/language-selector {:id        section-id
                                       :language  (:entity/language section)
                                       :on-change #(>evt [::vte/set-section-language
                                                          group-id
                                                          section-id
                                                          %])}]])]
      (when toplevel?
        [section-draft-selector (util/mmap tr section group-id)])
      (when (:verdict.section/required? section)
        [:div.ml-4.mb-2 [:span.text-ui-red.pr-1 "*"]
         (tr [:verdict.section/required?])])
      [:span (:verdict.section/id section)]]
     (when copy-of
       [:div
        [:div.bx--label "Copy of"]
        [:div.label-value
         [:a {:href     (str "#" (:db/id copy-of))
              :on-click #(js/setTimeout futil/scroll-to-anchor 10)}
          (str (section-title tr copy-of)
               (when-let [orig-lang (:entity/language copy-of)]
                 (str " (" (name orig-lang) ")")))]]])
     (when-not read-only?
       [:div
        ;; don't allow editing label in keyword format
        (when (some-> section
                      :verdict.section/label
                      (str/starts-with? ":")
                      not)
          (cc/button {:kind       :ghost
                      :class-name "max-w-none"
                      :icon       (if label-edit? i/remove i/pen)
                      :on-click   #(>evt [(if label-edit? ::cancel-edit-label ::edit-label) section-id])}
                     (if label-edit? (tr [:close]) (tr [:verdict/edit-label]))))
        (when (and (verdict-section/copyable-section-types type)
                   (not copy-of))
          (cc/button {:kind     :ghost
                      :icon     i/copy
                      :on-click #(>evt [::vte/copy-section group-id section-id])}
                     (tr [:copy])))
        (ui/modal-confirmation-button
          (util/mmap {:trigger-button-opts {:icon i/trash
                                            :kind :ghost}
                      :on-yes-click        #(>evt [::vte/delete-section
                                                   (<sub [::gas/group-id])
                                                   (-> section :db/id)])
                      :trigger-button-text (tr [:delete])}
                     tr))])]))

(rf/reg-event-fx ::reorder-sections
  (fn [_ [_ section-id from to]]
    {:comm/command {:command    :group/verdict-templates-reorder-inner-sections
                    :method     :post
                    :data       {:section-id section-id
                                 :sections   (dnd/ordering-tx-for-entity from to)}
                    :on-success [::entities/apply-changes]}}))

(defn- render-children [{:keys [tr read-only?] :as ctx} section]
  (r/with-let [*rearranging? (r/atom false)]
    [:div
     [:div.flex-row
      [:div.flex-1.text-right
       (when-not read-only?
         (cc/button
           {:on-click #(swap! *rearranging? not)
            :size     :small
            :kind     (if @*rearranging? :secondary :primary)}
           (if @*rearranging? (tr [:done]) (tr [:reorder-short]))))]]
     (let [sections (<sub [::verdict-template-edit/sections (:db/id section)])]
       (if @*rearranging?
         [dnd/wrap-dnd
          {:id         (str "sections-dnd-" (:db/id section))
           :items      sections
           :on-reorder #(>evt [::reorder-sections (:db/id section) %1 %2])}
          (fn [{:keys [sorted augment-target-props augment-draggable-props]}]
            (for-frag [sorted-section sorted]
              [:div.verdict-template-page.flex.justify-between
               (augment-target-props {} sorted-section)
               (section-title tr sorted-section)
               [:div.inline-block
                (augment-draggable-props {} sorted-section)
                (cc/icon {:icon        i/draggable
                          :description (tr [:reorder])})]]))]
         (for-frag [child sections]
           ^{:key (:db/id child)}
           (render ctx child))))]))

(defn sub-sections [{:keys [tr read-only?] :as ctx} {:verdict.section/keys [type] :as section}]
  [:<>
   [:h5 (tr [:admin/sections])]
   (when (seq (:entity/_parent section))
     ^{:key (str (:db/id section) "-children")}
     (render-children ctx section))
   (when-not read-only?
     [:div.mt-4
      [verdict-template-edit/add-section
       {:types       (if (verdict-section/types-with-editable-children type)
                       verdict-section/template-sub-types
                       verdict-section/static-sub-types)
        :label       (tr [:verdict/add-section])
        :parent-id   (:db/id section)
        :template-id (-> section
                         :verdict.section/template
                         :db/id)}]])])

(defmethod render :default [{:keys [tr group-id read-only?] :as ctx}
                            {:verdict.section/keys [type] :as section}]
  [verdict-template-edit/subsection-container
   [section-title-bar (util/mmap tr section group-id read-only?)]
   (when (verdict-section/sub-types-with-css type)
     (if read-only?
       [verdict-template-edit/css-view (util/mmap section)]
       [verdict-template-edit/css-editor (util/mmap section)]))
   (when (verdict-section/sub-types-with-children type)
     [sub-sections ctx section])])

(defn- section->placeholder [tr {:verdict.section/keys [id] :as section}]
  (when id
    {:id    (name id)
     :title (section-title tr section)}))

(defn- children->placeholders [tr parent]
  (let [children (:entity/_parent parent)]
    (concat (keep (partial section->placeholder tr) children)
            (mapcat (partial children->placeholders tr) children))))


(defn review-comment-placeholders [tr]
  [{:id    "review-comments"
    :title (tr [:review-comments/title])}
   {:id    "latest-review-comments"
    :title (tr [:latest-review-comments/title])}])


(defn template-page [{:keys [tr group-id images read-only?]
                      :as   ctx}
                     {:verdict.section/keys [html-template print-css]
                      id                    :db/id
                      :as                   section}]
  [template-editor/editor
   (-> {:html-template              html-template
        :print-css                  print-css
        :tr                         tr
        :placeholders               (->> (concat (children->placeholders tr (:entity/parent section))
                                                 (review-comment-placeholders tr))
                                         distinct
                                         (sort-by :id))
        :review-placeholder-options {:review-configs (<sub [::review-config/available-review-configs group-id])}
        :also-css                   (->> section
                                         eu/descendants
                                         (filter (comp not str/blank? :verdict.section/print-css))
                                         (map (partial section-title tr))
                                         sort)
        :images                     images
        :read-only?                 read-only?
        :title                      (section-title-bar (util/mmap tr section group-id read-only?))
        :on-save                    (fn [html css]
                                      (>evt [::vte/update-section
                                             group-id
                                             id
                                             {:verdict.section/html-template (or html "")
                                              :verdict.section/print-css     (or css "")}]))
        :preview-url                (file-download/download-href
                                      :group/generate-template-preview-pdf
                                      {:group-id           group-id
                                       :verdict-section-id id})}
       (text-editor/with-external-edit-state id))
   [sub-sections ctx section]])


(defmethod render :verdict.section.type/template-page [ctx section]
  [template-page ctx section])


(defmethod render :verdict.section.type/cover-page [ctx section]
  [template-page ctx section])


(defmethod render :verdict.section.type/static-page [{:keys [tr group-id read-only?]} section]
  [:div.verdict-template-page {:key (:db/id section)}
   [:div.w-full.flex.items-center>div.flex-1.items-center
    [section-title-bar (util/mmap tr group-id section read-only?)]]
   [:div.verdict-template__section-config.bx--row
    [:div.bx--col-xs-12
     (tr [:admin/pdf-upload-help])]]
   [:div.verdict-template__section-config.bx--row.items-center
    [:div.bx--col-xs-6
     [group-files/verdict-static-page-upload (:db/id section) group-id]]
    (when-let [file (-> (:verdict.section/entities section)
                        first)]
      [:div.bx--col-xs-6.flex.items-center
       (tr [:admin/current-file])
       " "
       (cc/link {:href (group-files/file-link group-id (:db/id file) true)}
                (:file/filename file))
       [ui/modal-confirmation-button
        {:tr                tr
         :id                "delete-file"
         :modal-title       (tr [:verdict.template/delete-file])
         :trigger-icon-opts {:icon        i/trash
                             :description (tr [:verdict.template/delete-file])
                             :class-name  "cursor-pointer ml-3"}
         :no-button-text    (tr [:cancel])
         :yes-button-text   (tr [:delete])
         :on-yes-click      (fn [_]
                              (>evt [::vte/update-section
                                     group-id
                                     (:db/id section)
                                     {:remove-file? true}
                                     [::group-files/delete-file group-id (:db/id file)]]))}
        [:p (tr [:verdict.template/delete-confirm]
                [(:file/filename file)])]]])]])


(defmethod render :verdict.section.type/form-field [{:keys [tr group-id read-only?]} section]
  (let [id              (str "form-field-section-" (:db/id section))
        form-select-id  (str id "-form-select")
        field-select-id (str id "-field-select")
        picked-form     (<sub [::selected-element form-select-id])
        picked-field    (<sub [::selected-element field-select-id])
        ;; Fall back to the persisted content so the saved value shows on (re)load;
        ;; once the user picks a form, the field is driven purely by their picks.
        saved           (<sub [::form-and-field-for-content (:verdict.section/content section)])
        selected-form   (or picked-form (:form saved))
        selected-field  (or picked-field
                            (when (nil? picked-form) (:field saved)))
        forms           (<sub [::form-names])
        fields          (<sub [::form-fields selected-form])
        lang            (<sub [:language])]
    [verdict-template-edit/subsection-container
     [section-title-bar (util/mmap tr section group-id read-only?)]
     (if read-only?
       [verdict-template-edit/css-view (util/mmap section)]
       [verdict-template-edit/css-editor (util/mmap section)])
     [:div.pt-2
      [:h5 (tr [:verdict.section/selected-form-field]) ": " (:verdict.section/content section)]
      [:div.flex.flex-row.items-start
       [:div.flex.p-2
        [ui-select/select {:id              form-select-id
                           :label-text      (tr [:verdict.section/select-form])
                           :values          forms
                           :value           selected-form
                           :allow-empty?    false
                           :disabled-values #{(tr [:select-option])}
                           :on-change       #(do
                                               (>evt [::remove-selection field-select-id])
                                               (>evt [::add-selection form-select-id %]))}]]
       (when (some? selected-form)
         [:div.flex.p-2
          [ui-select/select {:id              field-select-id
                             :label-text      (tr [:verdict.section/select-field])
                             :values          fields
                             :val->str        #(cond-> (or (form-model/label-text (:label %) lang)
                                                           (str (:field %)))
                                                 (= (:tag %) :table) (str " " (tr [:verdict/table])))
                             :value           selected-field
                             :allow-empty?    false
                             :disabled-values #{(tr [:select-option])}
                             :on-change       #(do
                                                 (>evt [::add-selection field-select-id %])
                                                 (>evt [::vte/update-section
                                                        group-id
                                                        (:db/id section)
                                                        {:verdict.section/content (:field %)}]))}]])]]]))


(defn- get-section-permit-type [section]
  (if-let [permit-type (:template/permit-type section)]
    permit-type
    (some-> section
            :entity/parent
            get-section-permit-type)))

(defmethod render :verdict.section.type/parties [{:keys [tr group-id read-only?]} section]
  (let [party-roles (if (<sub [::gas/superdomain-group?])
                      (<sub [::descendant-party-roles (get-section-permit-type section)])
                      (<sub [::party-roles group-id]))
        selected    (or (:verdict.section/content section)
                        (->> party-roles (map :role-key) set))
        values      (map :role-key party-roles)]
    [verdict-template-edit/subsection-container
     [section-title-bar (util/mmap tr section group-id read-only?)]
     (if read-only?
       [verdict-template-edit/css-view (util/mmap section)]
       [verdict-template-edit/css-editor (util/mmap section)])
     [:div.pt-2
      (cc/accordion
        {}
        (cc/accordion-item
          {:title (tr [:verdict.section/parties-available])}
          [ui-ms/multi-checkbox
           (util/mmap {:id        (str "verdict-template-parties-" (:db/id section))
                       :val->str  #(-> party-roles (p/find-first {:role-key %}) :label)
                       :on-change #(>evt [::vte/update-section
                                          group-id
                                          (:db/id section)
                                          {:verdict.section/content %}])}
                      selected values tr read-only?)]))]]))


(defmethod render :verdict.section.type/single-party [{:keys [tr group-id read-only?]} section]
  (let [section-id  (:db/id section)
        checked?    (->> section :verdict.section/content :hide-business-id? boolean)
        permit-type (->> section :verdict.section/template :domain/authority-group group-model/primary-permit-type)]
    [verdict-template-edit/subsection-container
     [section-title-bar (util/mmap tr section group-id read-only?)]
     (if read-only?
       [verdict-template-edit/css-view (util/mmap section)]
       [verdict-template-edit/css-editor (util/mmap section)])
     (when (and (= (:verdict.section/id section) :issued-to) (= permit-type :permit-type/L))
       [:div.pt-2
        (cc/checkbox {:id         (str group-id "-hide-business-id-checkbox-" section-id)
                      :label-text (tr [:verdict.section/hide-business-id])
                      :checked    checked?
                      :on-change  #(>evt [::vte/update-section
                                          group-id
                                          (:db/id section)
                                          {:verdict.section/content {:hide-business-id? %}}])})])]))
